import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowRight, Star } from "lucide-react";

const GEO_URL = "/us-states.json";
const TEXAS_ID = "48";

const MARKETS = [
  { name: "Houston MSA", coords: [-95.3698, 29.7604], hq: true, desc: "Our home market. Harris, Fort Bend, Montgomery & Brazoria counties — multifamily and industrial focus." },
  { name: "Dallas–Fort Worth", coords: [-96.7970, 32.7767], desc: "The fastest-growing commercial market in the U.S. Multifamily and mixed-use." },
  { name: "Austin", coords: [-97.7431, 30.2672], desc: "Tech-driven demand and value-add multifamily across the metro." },
  { name: "San Antonio", coords: [-98.4936, 29.4241], desc: "Steady fundamentals and durable workforce housing opportunities." },
  { name: "Phoenix", coords: [-112.0740, 33.4484], desc: "Sun Belt multifamily growth market with strong in-migration." },
  { name: "Atlanta", coords: [-84.3880, 33.7490], desc: "Southeast multifamily expansion market and logistics hub." },
];

export default function TexasMarketsMap({ onSubmit }) {
  const [active, setActive] = useState(0);

  return (
    <section className="relative overflow-hidden bg-emerald-950 py-16 sm:py-24" data-testid="markets-map">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: "60px 60px" }} />
      <div className="pointer-events-none absolute left-1/3 top-1/2 h-[460px] w-[460px] -translate-y-1/2 rounded-full bg-emerald-500/10 blur-[150px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <div className="flex items-center gap-2 text-gold">
            <MapPin className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.35em]">Our Markets</span>
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-cream sm:text-4xl md:text-5xl">
            Texas-Based. <span className="luxury-text-gradient">Nationally Active.</span>
          </h2>
          <p className="mt-4 text-sm leading-7 text-cream/70 sm:text-base">
            Headquartered in Houston with active acquisitions across the Sun Belt. Explore our primary markets.
          </p>
        </motion.div>

        <div className="mt-10 grid items-center gap-8 lg:grid-cols-12">
          {/* Map */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="lg:col-span-7" data-testid="markets-map-svg">
            <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 1000 }} width={800} height={500} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const isTexas = geo.id === TEXAS_ID;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: {
                            fill: isTexas ? "#1B4332" : "#0c2c23",
                            stroke: isTexas ? "#E5C98B" : "#1f5040",
                            strokeWidth: isTexas ? 1.1 : 0.5,
                            outline: "none",
                          },
                          hover: { fill: isTexas ? "#2D6A4F" : "#103a2e", stroke: "#E5C98B", strokeWidth: 0.8, outline: "none" },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {MARKETS.map((m, i) => (
                <Marker key={m.name} coordinates={m.coords} onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)} style={{ cursor: "pointer" }}>
                  {/* glow ring */}
                  <circle r={active === i ? 14 : 9} fill="#E5C98B" opacity={active === i ? 0.25 : 0.14}>
                    <animate attributeName="r" values={`${active === i ? 9 : 6};${active === i ? 16 : 11};${active === i ? 9 : 6}`} dur="2.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.28;0.06;0.28" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  {/* dot */}
                  <circle r={active === i ? 5 : 3.5} fill="#E5C98B" stroke="#fff" strokeWidth={active === i ? 1.2 : 0.6} />
                  {(active === i || m.hq) && (
                    <text textAnchor="middle" y={-14} className="select-none"
                      style={{ fill: "#F4ECD8", fontSize: active === i ? 13 : 11, fontWeight: 700, paintOrder: "stroke", stroke: "#04241b", strokeWidth: 3 }}>
                      {m.name}{m.hq ? " ★" : ""}
                    </text>
                  )}
                </Marker>
              ))}
            </ComposableMap>
            <p className="mt-2 text-center text-xs italic text-cream/40">We actively pursue acquisitions in all 50 states.</p>
          </motion.div>

          {/* Market list + detail */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-2.5">
              {MARKETS.map((m, i) => (
                <button key={m.name} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)}
                  data-testid={`market-pin-${i}`}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-all ${
                    active === i
                      ? "border-gold/60 bg-emerald-800/50 shadow-[0_0_20px_rgba(229,201,139,0.15)]"
                      : "border-emerald-800/50 bg-emerald-900/30 hover:border-emerald-600/60"
                  }`}>
                  <MapPin className={`h-3.5 w-3.5 shrink-0 ${active === i ? "text-gold" : "text-cream/40"}`} />
                  <span className={`text-xs font-semibold ${active === i ? "text-cream" : "text-cream/70"}`}>{m.name}</span>
                  {m.hq && <Star className="ml-auto h-3 w-3 fill-gold text-gold" />}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                className="mt-4 rounded-2xl border border-emerald-700/40 bg-emerald-900/40 p-5 backdrop-blur-sm" data-testid="market-detail">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-cream">{MARKETS[active].name}</h3>
                  {MARKETS[active].hq && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold">Headquarters</span>}
                </div>
                <p className="mt-2 text-sm leading-6 text-cream/70">{MARKETS[active].desc}</p>
              </motion.div>
            </AnimatePresence>

            <button onClick={onSubmit} data-testid="markets-cta"
              className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-emerald-950 transition-all hover:shadow-[0_8px_30px_rgba(229,201,139,0.35)]">
              Have a Property in One of These Markets?
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
